import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { cn } from '@core/utils/cn';
import { FormSection } from '../../../../components/form-section';
import { Spinner } from '../../../../components/spinner';
import {
    AccountOperationCauseType,
    AccountOperationPurchaseDto,
    ProductOrderState,
    VendorSource
} from '../../../../core/api/WalletClient';
import { Api } from '../../../../core/api';
import { I18n } from '../../../../core/i18n';
import { injectTSDI } from '../../../../core/tsdi';
import { useOperationDetailsStore } from './store';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getErrorMessage(error: any): string {
    if (error && typeof error === 'object' && 'error' in error) {
        const errorObject = error.error;
        if (errorObject?.message?.defaultMessage) {
            return errorObject.message.defaultMessage;
        }
        if (errorObject?.defaultMessage) {
            return errorObject.defaultMessage;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An unknown error occurred';
}

const Field = ({
    label,
    children,
    className
}: {
    label: string;
    children: React.ReactNode;
    className?: string;
}) => (
    <div className={cn('flex flex-col gap-0.5', className)}>
        <span className="text-sm font-medium text-muted-foreground">
            {label}
        </span>
        <div className="text-sm">{children}</div>
    </div>
);

const AccountOperationDetails = observer(() => {
    const { operationId } = useParams();
    const { formatDateTime } = injectTSDI(I18n);
    const { operationDetails } = useOperationDetailsStore();

    React.useEffect(() => {
        if (operationId) {
            operationDetails.request([Number(operationId)]);
        }
    }, [operationId]);

    if (operationDetails.loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (operationDetails.error) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    Error loading operation details:{' '}
                    {getErrorMessage(operationDetails.error)}
                </AlertDescription>
            </Alert>
        );
    }

    const operation = operationDetails.result;

    if (!operation) {
        return null;
    }

    return (
        <div className="grid gap-6 md:grid-cols-2">
            <FormSection title="Basic Information">
                <div className="flex flex-col gap-4">
                    <Field label="Operation ID">{operation.id}</Field>
                    <Field label="Timestamp">
                        {formatDateTime(operation.timestamp)}
                    </Field>
                    <Field label="Value">
                        <span
                            className={cn(
                                'text-lg font-semibold',
                                operation.value.value >= 0
                                    ? 'text-success'
                                    : 'text-destructive'
                            )}
                        >
                            {operation.value.value >= 0 ? '+' : ''}
                            {operation.value.value}{' '}
                            {operation.value.currency.symbol}
                        </span>
                    </Field>
                    <Field label="Cause Type">
                        <Badge
                            variant={getCauseTypeVariant(operation.cause.type)}
                        >
                            {operation.cause.type}
                        </Badge>
                    </Field>
                </div>
            </FormSection>

            <FormSection title="Cause Details">
                {operation.cause.type ===
                    AccountOperationCauseType.GOAL_REACHED &&
                    operation.cause.goal && (
                        <Field label="Goal Value">
                            {operation.cause.goal.value}
                        </Field>
                    )}

                {operation.cause.type === AccountOperationCauseType.GRANT &&
                    operation.cause.userId && (
                        <Field label="Granted By User ID">
                            {operation.cause.userId}
                        </Field>
                    )}

                {operation.cause.type === AccountOperationCauseType.PURCHASE &&
                    operation.cause.purchase && (
                        <PurchaseDetails
                            purchase={operation.cause.purchase}
                            onRetrySuccess={() =>
                                operationDetails.request([Number(operationId)])
                            }
                        />
                    )}

                {!operation.cause.goal &&
                    !operation.cause.userId &&
                    !operation.cause.purchase && (
                        <p className="text-sm text-muted-foreground">
                            No additional details available
                        </p>
                    )}
            </FormSection>
        </div>
    );
});

function getCauseTypeVariant(
    type: AccountOperationCauseType
): 'success' | 'info' | 'warning' | 'muted' {
    switch (type) {
        case AccountOperationCauseType.GOAL_REACHED:
            return 'success';
        case AccountOperationCauseType.GRANT:
            return 'info';
        case AccountOperationCauseType.PURCHASE:
            return 'warning';
        default:
            return 'muted';
    }
}

function getOrderStatusVariant(
    status: ProductOrderState
): 'success' | 'info' | 'destructive' | 'warning' | 'muted' {
    switch (status) {
        case ProductOrderState.SUCCESS:
            return 'success';
        case ProductOrderState.PENDING:
            return 'info';
        case ProductOrderState.CANCELED:
            return 'warning';
        case ProductOrderState.ERROR:
            return 'destructive';
        default:
            return 'muted';
    }
}

interface PurchaseDetailsProps {
    purchase: AccountOperationPurchaseDto;
    onRetrySuccess?: () => void;
}

const ExternalLink = ({
    href,
    children
}: {
    href: string;
    children: React.ReactNode;
}) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-primary underline-offset-4 hover:underline"
    >
        {children}
    </a>
);

function PurchaseDetails({ purchase, onRetrySuccess }: PurchaseDetailsProps) {
    const { walletClient } = injectTSDI(Api);
    const [retryLoading, setRetryLoading] = React.useState(false);
    const [retryError, setRetryError] = React.useState<string | null>(null);
    const [retrySuccess, setRetrySuccess] = React.useState(false);

    const [syncLoading, setSyncLoading] = React.useState(false);
    const [syncError, setSyncError] = React.useState<string | null>(null);
    const [syncSuccess, setSyncSuccess] = React.useState(false);

    const handleRetryPurchase = async () => {
        setRetryLoading(true);
        setRetryError(null);
        setRetrySuccess(false);
        try {
            await walletClient.supportApi.purchaseSupportControllerRetryPurchase(
                purchase.id,
                { force: false }
            );
            setRetrySuccess(true);
            onRetrySuccess?.();
        } catch (error) {
            setRetryError(
                error instanceof Error
                    ? error.message
                    : 'Failed to retry purchase'
            );
        } finally {
            setRetryLoading(false);
        }
    };

    const showRetryButton =
        purchase.order?.status === ProductOrderState.ERROR && !retrySuccess;

    const handleSyncPurchase = async () => {
        setSyncLoading(true);
        setSyncError(null);
        setSyncSuccess(false);
        try {
            await walletClient.supportApi.purchaseSupportControllerSyncPurchase(
                purchase.id
            );
            setSyncSuccess(true);
            onRetrySuccess?.();
        } catch (error) {
            setSyncError(getErrorMessage(error));
        } finally {
            setSyncLoading(false);
        }
    };

    const showSyncButton =
        purchase.order?.status === ProductOrderState.PENDING &&
        purchase.vendor.source === VendorSource.CADOOZ &&
        !syncSuccess;

    const voucher = purchase.order?.voucher;

    return (
        <div className="flex flex-col gap-4">
            <Field label="Vendor">
                <p>{purchase.vendor.name}</p>
                <span className="text-xs text-muted-foreground">
                    ID: {purchase.vendor.id}
                </span>
            </Field>

            <Separator />

            <Field label="Product">
                <p>{purchase.product.variant}</p>
                <p className="text-muted-foreground">
                    Type: {purchase.product.productType}
                </p>
                <p>
                    Price: {purchase.product.price.value}{' '}
                    {purchase.product.price.currency.symbol}
                </p>
                <span className="text-xs text-muted-foreground">
                    Product ID: {purchase.product.id}
                </span>
            </Field>

            {purchase.order && (
                <>
                    <Separator />
                    <Field label="Order">
                        <div className="flex items-center gap-2">
                            <span>ID: {purchase.order.id}</span>
                            <Badge
                                variant={getOrderStatusVariant(
                                    purchase.order.status
                                )}
                            >
                                {purchase.order.status}
                            </Badge>
                        </div>
                    </Field>

                    {voucher && (
                        <Field label="Voucher Details">
                            <div className="mt-1 flex flex-col gap-1 rounded-md bg-muted p-3">
                                <p>
                                    <strong>Vendor:</strong>{' '}
                                    {voucher.vendorName}
                                </p>
                                {voucher.code && (
                                    <p>
                                        <strong>Code:</strong> {voucher.code}
                                    </p>
                                )}
                                {voucher.pin && (
                                    <p>
                                        <strong>PIN:</strong> {voucher.pin}
                                    </p>
                                )}
                                {voucher.externalPlatformLink && (
                                    <p>
                                        <strong>External Link:</strong>{' '}
                                        <ExternalLink
                                            href={voucher.externalPlatformLink}
                                        >
                                            {voucher.externalPlatformLink}
                                        </ExternalLink>
                                    </p>
                                )}
                                {voucher.voucherFileUrl && (
                                    <p>
                                        <strong>Voucher File:</strong>{' '}
                                        <ExternalLink
                                            href={voucher.voucherFileUrl}
                                        >
                                            Download
                                        </ExternalLink>
                                    </p>
                                )}
                                {voucher.barcodeFileUrl && (
                                    <p>
                                        <strong>Barcode:</strong>{' '}
                                        <ExternalLink
                                            href={voucher.barcodeFileUrl}
                                        >
                                            View Barcode
                                        </ExternalLink>
                                    </p>
                                )}
                            </div>
                        </Field>
                    )}

                    {showRetryButton && (
                        <>
                            <Separator />
                            <div>
                                <Button
                                    className="bg-warning text-warning-foreground hover:bg-warning/90"
                                    onClick={handleRetryPurchase}
                                    loading={retryLoading}
                                >
                                    {retryLoading
                                        ? 'Retrying...'
                                        : 'Retry Purchase'}
                                </Button>
                            </div>
                        </>
                    )}

                    {retryError && (
                        <p className="text-sm text-destructive">{retryError}</p>
                    )}

                    {retrySuccess && (
                        <div>
                            <Badge variant="success">
                                Purchase retry initiated successfully
                            </Badge>
                        </div>
                    )}

                    {showSyncButton && (
                        <>
                            <Separator />
                            <div>
                                <Button
                                    className="bg-info text-info-foreground hover:bg-info/90"
                                    onClick={handleSyncPurchase}
                                    loading={syncLoading}
                                >
                                    {syncLoading
                                        ? 'Syncing...'
                                        : 'Sync Purchase'}
                                </Button>
                            </div>
                        </>
                    )}

                    {syncError && (
                        <p className="text-sm text-destructive">{syncError}</p>
                    )}

                    {syncSuccess && (
                        <div>
                            <Badge variant="success">
                                Purchase sync initiated successfully
                            </Badge>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default AccountOperationDetails;
